<script setup lang="ts">
import { SolButton } from '@solfacil/girassol'
import { useI18n } from 'vue-i18n'

interface Props {
  id: string
  forbidden?: boolean
  headingTag?: 'h3' | 'h4'
}

const props = withDefaults(defineProps<Props>(), {
  forbidden: false,
  headingTag: 'h3',
})

const emit = defineEmits<{
  retry: []
}>()

const { t } = useI18n()

const ERROR_CONTENT = {
  forbidden: {
    image: '/images/not-impersonate-overview.svg',
    titleKey: 'my_wallet.forbidden.title',
    descriptionKey: 'my_wallet.forbidden.description',
    canRetry: false,
  },
  generic: {
    image: '/images/empty-state-error.svg',
    titleKey: 'my_wallet.error.title',
    descriptionKey: 'my_wallet.error.description',
    canRetry: true,
  },
} as const

const errorContent = computed(() => ERROR_CONTENT[props.forbidden ? 'forbidden' : 'generic'])
</script>

<template>
  <div :id="id" class="my-wallet-error" role="alert">
    <img
      :src="errorContent.image"
      alt=""
      class="my-wallet-error-image"
    >
    <div class="my-wallet-error-text">
      <component :is="headingTag" class="my-wallet-error-title">
        {{ t(errorContent.titleKey) }}
      </component>
      <p class="my-wallet-error-description">
        {{ t(errorContent.descriptionKey) }}
      </p>
    </div>
    <SolButton
      v-if="errorContent.canRetry"
      :id="`${id}-retry`"
      variant="secondary"
      @click="emit('retry')"
    >
      {{ t('my_wallet.error.retry') }}
    </SolButton>
  </div>
</template>

<style scoped lang="scss">
.my-wallet-error {
  @apply flex flex-col items-center gap-6 py-8 mx-auto text-center;
  max-width: 550px;

  .my-wallet-error-image {
    @apply w-full h-auto;
    max-width: 105px;
  }

  .my-wallet-error-text {
    @apply flex flex-col gap-2;
  }

  .my-wallet-error-title {
    @apply fonts-heading-h4 text-neutral-low-pure;
  }

  .my-wallet-error-description {
    @apply fonts-body-large-regular text-neutral-low-light;
  }
}
</style>
